// components/TenantFormPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { insert_tenant_with_files, getPGById } from '../apis/services/pgApi';
import '../scss/components/_tenantform.scss';
import CryptoJS from 'crypto-js';

// File Upload Component
const FileInput = ({ label, accept, value, onChange, hint, fieldName, required }) => {
  const ref = useRef();
  const [preview, setPreview] = useState(null);

  const handleFileChange = async (file) => {
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }
      onChange(file, fieldName);
    } else {
      setPreview(null);
      onChange(null, fieldName);
    }
  };

  return (
    <div className="tf-file-wrap">
      <label className="tf-label">
        {label} {required && <span className="tf-req">*</span>}
      </label>
      <div className={`tf-file-box ${value ? 'has-file' : ''}`} onClick={() => ref.current.click()}>
        <input 
          ref={ref} 
          type="file" 
          accept={accept} 
          style={{ display: 'none' }}
          onChange={e => handleFileChange(e.target.files[0] || null)} 
        />
        {value ? (
          <div className="tf-file-chosen">
            {preview && (
              <img src={preview} alt="Preview" className="tf-file-preview" />
            )}
            <span className="tf-file-name">{value.name}</span>
            <button className="tf-file-clear" onClick={e => { e.stopPropagation(); handleFileChange(null) }}>×</button>
          </div>
        ) : (
          <div className="tf-file-empty">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span>{hint || 'Click to upload'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const TenantFormPage = () => {

  const [pgId, setPgId] = useState(null);

  useEffect(() => {
    // URL se encrypted parameter lo
    const path = window.location.pathname;
    const encryptedParam = path.split('/').pop(); // /tenant-form/encrypted-data-here
    console.log("encryptedParam",encryptedParam);
    
    // Decrypt karo
    const decryptedPgId = decryptPgId(encryptedParam);
    
    // ✅ Console mein dikhao
    console.log('🔓 Decrypted PG ID:', decryptedPgId);
    
    setPgId(decryptedPgId);
    
    // Ab form submit karte time ye PG ID use karo
  }, []);

  const decryptPgId = (encryptedData) => {
    try {
      // URL decode
      const decoded = decodeURIComponent(encryptedData);
      console.log("Decoded:", decoded);
      
      // Split IV and encrypted data
      const [ivHex, encryptedHex] = decoded.split(':');
      
      console.log("IV Hex:", ivHex);
      console.log("Encrypted Hex:", encryptedHex);
      
      // Convert hex to WordArray
      const iv = CryptoJS.enc.Hex.parse(ivHex);
      const encrypted = CryptoJS.enc.Hex.parse(encryptedHex);
      
      // Secret key - same as backend (sha256 of 'cipl')
      const secretKey = 'cipl';
      const key = CryptoJS.SHA256(secretKey);
      
      // Create encrypted object
      const encryptedObj = CryptoJS.lib.CipherParams.create({
        ciphertext: encrypted,
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
        key: key
      });
      
      // Decrypt
      const decrypted = CryptoJS.AES.decrypt(encryptedObj, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      
      const result = decrypted.toString(CryptoJS.enc.Utf8);
      console.log('🔓 Decrypted:', result);
      return result;
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  };

  const navigate = useNavigate();
  const [pgDetails, setPgDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form state - UPDATED with new fields
  const [form, setForm] = useState({
    title: '',           // NEW: Title field
    name: '', 
    fatherName: '',      // NEW: Father's Name
    age: '', 
    dob: '', 
    gender: '', 
    phone: '', 
    email: '',
    presentAddress: '', 
    permanentAddress: '',
    pincode: '',         // NEW: Pincode field
    aadharNumber: '',    // NEW: Aadhar Number field
    emergencyName: '', 
    emergencyPhone: '', 
    emergencyRelation: '',
    profession: '', 
    checkInDate: '',
    aadhaarFile: null, 
    photoFile: null, 
    idFile: null,
  });

  const [errors, setErrors] = useState({});

  // Fetch PG details on load
  useEffect(() => {
    const fetchPGDetails = async () => {
      try {
        setLoading(true);
        const result = await getPGById(pgId);
        if (result.PGID ) {
          setPgDetails(result);
        } else {
          setApiError('PG not found. Please check the link.');
        }
      } catch (error) {
        console.error('Error fetching PG details:', error);
        setApiError('Failed to load PG details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (pgId) {
      fetchPGDetails();
    }
  }, [pgId]);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: undefined }));
  };

  const setFile = (file, fieldName) => {
    setForm(p => ({ ...p, [fieldName]: file }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.phone.trim()) e.phone = 'Mobile number is required';
    if (!form.email.trim()) e.email = 'Email is required';
    if (!form.checkInDate) e.checkInDate = 'Check-in date is required';
    // if (!form.gender) e.gender = 'Please select gender';
    if (!form.aadharNumber) e.aadharNumber = 'Aadhar number is required';
    
    // Optional validation for Aadhar Number (if provided)
    if (form.aadharNumber && form.aadharNumber.trim()) {
      const aadhar = form.aadharNumber.replace(/\s/g, '');
      if (!/^\d{12}$/.test(aadhar)) {
        e.aadharNumber = 'Aadhar must be 12 digits';
      }
    }
    
    // Optional validation for Pincode (if provided)
    if (form.pincode && form.pincode.trim()) {
      const pincode = form.pincode.replace(/\s/g, '');
      if (!/^\d{6}$/.test(pincode)) {
        e.pincode = 'Pincode must be 6 digits';
      }
    }
    
    return e;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!pgId) {
      setApiError('Invalid PG. Please try again.');
      return;
    }

    setSubmitting(true);
    setApiError(null);

    try {
      const formData = new FormData();

      const tenantData = {
        PGID: parseInt(pgId),
        Title: form.title || null,                    // NEW: Title
        TenantName: form.name,
        TenantFatherName: form.fatherName || null,    // NEW: Father's Name
        Tenantage: form.age ? parseInt(form.age) : null,
        TenantDOB: form.dob || null,
        Gender: form.gender ? form.gender.charAt(0).toUpperCase() + form.gender.slice(1) : null,
        PhoneNumber: form.phone,
        Email1: form.email || null,
        PresentAddress: form.presentAddress || null,
        PermanentAddress: form.permanentAddress || null,
        Pincode: form.pincode || null,                // NEW: Pincode
        AadharNumber: form.aadharNumber || null,      // NEW: Aadhar Number
        EmergencyContactRelation: form.emergencyRelation || null,
        EmergencyContactPersonName: form.emergencyName || null,
        EmergencyContactNumber: form.emergencyPhone || null,
        TenantProfession: form.profession || null,
        CheckInDate: form.checkInDate,
        JoiningDate: form.checkInDate,
        Status: 'NEW',
      };

      formData.append('insertDto', JSON.stringify(tenantData));

      if (form.aadhaarFile) formData.append('aadhaar', form.aadhaarFile);
      if (form.photoFile) formData.append('photo', form.photoFile);
      if (form.idFile) formData.append('workingId', form.idFile);

      const result = await insert_tenant_with_files(formData);

      if (result && (result.Success === 1 || result.statusCode === 201)) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else if (result && result.statusCode === 413) {
        setApiError('File size exceeds 5MB limit. Please upload smaller files.');
      } else {
        // Show the error message from API
        let errorMsg = result?.Message || result?.message || 'Failed to add tenant. Please try again.';
        
        // Make it user-friendly for duplicate phone
        if (errorMsg && errorMsg.includes('Violation of UNIQUE KEY')) {
          errorMsg = 'This phone number is already registered. Please use a different phone number.';
        }
        
        setApiError(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting tenant:', error);
      
      let errorMsg = error.message || 'Failed to add tenant. Please try again.';
      
      // Make it user-friendly for duplicate phone
      if (errorMsg && errorMsg.includes('Violation of UNIQUE KEY')) {
        errorMsg = 'This phone number is already registered. Please use a different phone number.';
      }
      
      setApiError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="tf-loader-container">
        <div className="tf-loader"></div>
        <p>Loading form...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="tf-success-container">
        <div className="tf-success-card">
          <div className="tf-success-icon">✓</div>
          <h2>Application Submitted Successfully!</h2>
          <p>Thank you for completing your registration. The property owner will review your application and contact you shortly.</p>
          <button className="tf-btn tf-btn-primary" onClick={() => navigate('/')}>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tf-container">
      <div className="tf-wrapper">
        {/* Header */}
        <div className="tf-header">
          <h1>Tenant Registration Form</h1>
          {pgDetails && (
            <div className="tf-pg-info">
              <strong>{pgDetails.PGName}</strong>
              <span>{pgDetails.PGCity}, {pgDetails.PGLocality}</span>
            </div>
          )}
        </div>

        {/* Error Display */}
        {apiError && (
          <div className="tf-error-box">
            <strong>Error:</strong> {apiError}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* Personal Information */}
          <div className="tf-section">
            <h3 className="tf-section-title">Personal Information</h3>
            <div className="tf-grid">
              {/* Title - NEW field at the start */}
              <div className="tf-field">
                <label className="tf-label">Title</label>
                <input 
                  type="text" 
                  className="tf-input" 
                  placeholder="Enter title"
                  value={form.title} 
                  onChange={e => set('title', e.target.value)} 
                />
              </div>

              <div className={`tf-field ${errors.name ? 'tf-field-error' : ''}`}>
                <label className="tf-label">Full Name <span className="tf-req">*</span></label>
                <input 
                  type="text" 
                  className="tf-input" 
                  placeholder="Enter your full name"
                  value={form.name} 
                  onChange={e => set('name', e.target.value)} 
                />
                {errors.name && <span className="tf-error">{errors.name}</span>}
              </div>

              {/* Father's Name - NEW field */}
              <div className="tf-field">
                <label className="tf-label">Father's Name</label>
                <input 
                  type="text" 
                  className="tf-input" 
                  placeholder="Enter father's name"
                  value={form.fatherName} 
                  onChange={e => set('fatherName', e.target.value)} 
                />
              </div>

              <div className="tf-field">
                <label className="tf-label">Age</label>
                <input 
                  type="number" 
                  className="tf-input" 
                  placeholder="Age"
                  value={form.age} 
                  onChange={e => set('age', e.target.value)} 
                />
              </div>

              <div className="tf-field">
                <label className="tf-label">Date of Birth</label>
                <input 
                  type="date" 
                  className="tf-input" 
                  value={form.dob} 
                  onChange={e => set('dob', e.target.value)} 
                />
              </div>

              <div className="tf-field">
                <label className="tf-label">Gender
                   {/* <span className="tf-req">*</span> */}
                </label>
                <select className="tf-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {/* {errors.gender && <span className="tf-error">{errors.gender}</span>} */}
              </div>

              {/* Aadhar Number - NEW field */}
              <div className={`tf-field ${errors.aadharNumber ? 'tf-field-error' : ''}`}>
                <label className="tf-label">Aadhar Number <span className="atm-req">*</span></label>
                <input 
                  type="text" 
                  className="tf-input" 
                  placeholder="Enter 12-digit Aadhar number"
                  value={form.aadharNumber} 
                  onChange={e => {
                    const value = e.target.value.replace(/\s/g, '');
                    if (value === '' || /^\d{0,12}$/.test(value)) {
                      set('aadharNumber', value);
                    }
                  }}
                  maxLength="12"
                />
                {errors.aadharNumber && <span className="tf-error">{errors.aadharNumber}</span>}
              </div>

              <div className={`tf-field ${errors.phone ? 'tf-field-error' : ''}`}>
                <label className="tf-label">Mobile Number <span className="tf-req">*</span></label>
                <input 
                  type="tel" 
                  className="tf-input" 
                  placeholder="9876543210"
                  value={form.phone} 
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) set('phone', value);
                  }}
                />
                {errors.phone && <span className="tf-error">{errors.phone}</span>}
              </div>

              <div className={`tf-field ${errors.email ? 'tf-field-error' : ''}`}>
                <label className="tf-label">Email Address <span className="tf-req">*</span></label>
                <input 
                  type="email" 
                  className="tf-input" 
                  placeholder="your@email.com"
                  value={form.email} 
                  onChange={e => set('email', e.target.value)} 
                />
                {errors.email && <span className="tf-error">{errors.email}</span>}
              </div>

              <div className="tf-field tf-field-full">
                <label className="tf-label">Present Address</label>
                <textarea 
                  className="tf-textarea" 
                  rows="3" 
                  placeholder="House No., Street, City, State, PIN Code"
                  value={form.presentAddress} 
                  onChange={e => set('presentAddress', e.target.value)} 
                />
              </div>

              <div className="tf-field tf-field-full">
                <label className="tf-label">Permanent Address</label>
                <textarea 
                  className="tf-textarea" 
                  rows="3" 
                  placeholder="House No., Street, City, State, PIN Code"
                  value={form.permanentAddress} 
                  onChange={e => set('permanentAddress', e.target.value)} 
                />
              </div>

              {/* Pincode - NEW field */}
              <div className={`tf-field ${errors.pincode ? 'tf-field-error' : ''}`}>
                <label className="tf-label">Pincode</label>
                <input 
                  type="text" 
                  className="tf-input" 
                  placeholder="Enter 6-digit pincode"
                  value={form.pincode} 
                  onChange={e => {
                    const value = e.target.value.replace(/\s/g, '');
                    if (value === '' || /^\d{0,6}$/.test(value)) {
                      set('pincode', value);
                    }
                  }}
                  maxLength="6"
                />
                {errors.pincode && <span className="tf-error">{errors.pincode}</span>}
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="tf-section">
            <h3 className="tf-section-title">Emergency Contact</h3>
            <div className="tf-grid">
              <div className="tf-field">
                <label className="tf-label">Contact Person Name</label>
                <input 
                  type="text" 
                  className="tf-input" 
                  placeholder="Full name"
                  value={form.emergencyName} 
                  onChange={e => set('emergencyName', e.target.value)} 
                />
              </div>

              <div className="tf-field">
                <label className="tf-label">Contact Number</label>
                <input 
                  type="tel" 
                  className="tf-input" 
                  placeholder="9876543210"
                  value={form.emergencyPhone} 
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) set('emergencyPhone', value);
                  }}
                />
              </div>

              <div className="tf-field">
                <label className="tf-label">Relation</label>
                <select className="tf-select" value={form.emergencyRelation} onChange={e => set('emergencyRelation', e.target.value)}>
                  <option value="">Select relation</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="spouse">Spouse</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Professional Details */}
          <div className="tf-section">
            <h3 className="tf-section-title">Professional Details</h3>
            <div className="tf-grid">
              <div className="tf-field">
                <label className="tf-label">Profession</label>
                <select className="tf-select" value={form.profession} onChange={e => set('profession', e.target.value)}>
                  <option value="">Select profession</option>
                  <option value="student">Student</option>
                  <option value="working">Working Professional</option>
                  <option value="business">Business / Self-employed</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className={`tf-field ${errors.checkInDate ? 'tf-field-error' : ''}`}>
                <label className="tf-label">Expected Check-in Date <span className="tf-req">*</span></label>
                <input 
                  type="date" 
                  className="tf-input" 
                  value={form.checkInDate} 
                  onChange={e => set('checkInDate', e.target.value)} 
                />
                {errors.checkInDate && <span className="tf-error">{errors.checkInDate}</span>}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="tf-section">
            <h3 className="tf-section-title">Documents</h3>
            <div className="tf-grid tf-grid-3">
              <FileInput
                label="Aadhaar Card"
                accept=".pdf,.jpg,.jpeg,.png"
                value={form.aadhaarFile}
                onChange={setFile}
                fieldName="aadhaarFile"
                hint="PDF or Image (Max 5MB)"
              />
              <FileInput
                label="Passport Size Photo"
                accept=".jpg,.jpeg,.png"
                value={form.photoFile}
                onChange={setFile}
                fieldName="photoFile"
                hint="JPG/PNG (Max 5MB)"
              />
              <FileInput
                label="Work/Student ID Card"
                accept=".pdf,.jpg,.jpeg,.png"
                value={form.idFile}
                onChange={setFile}
                fieldName="idFile"
                hint="PDF or Image (Max 5MB)"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="tf-footer">
            <button type="button" className="tf-btn tf-btn-outline" onClick={() => navigate('/')}>
              Cancel
            </button>
            <button type="submit" className="tf-btn tf-btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="tf-spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantFormPage;

// // components/TenantFormPage.jsx
// import React, { useState, useRef, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { insert_tenant_with_files, getPGById } from '../apis/services/pgApi';
// import '../scss/components/_tenantform.scss';
// import CryptoJS from 'crypto-js';

// // File Upload Component
// const FileInput = ({ label, accept, value, onChange, hint, fieldName, required }) => {
//   const ref = useRef();
//   const [preview, setPreview] = useState(null);

//   const handleFileChange = async (file) => {
//     if (file) {
//       if (file.type.startsWith('image/')) {
//         const reader = new FileReader();
//         reader.onloadend = () => {
//           setPreview(reader.result);
//         };
//         reader.readAsDataURL(file);
//       } else {
//         setPreview(null);
//       }
//       onChange(file, fieldName);
//     } else {
//       setPreview(null);
//       onChange(null, fieldName);
//     }
//   };

//   return (
//     <div className="tf-file-wrap">
//       <label className="tf-label">
//         {label} {required && <span className="tf-req">*</span>}
//       </label>
//       <div className={`tf-file-box ${value ? 'has-file' : ''}`} onClick={() => ref.current.click()}>
//         <input 
//           ref={ref} 
//           type="file" 
//           accept={accept} 
//           style={{ display: 'none' }}
//           onChange={e => handleFileChange(e.target.files[0] || null)} 
//         />
//         {value ? (
//           <div className="tf-file-chosen">
//             {preview && (
//               <img src={preview} alt="Preview" className="tf-file-preview" />
//             )}
//             <span className="tf-file-name">{value.name}</span>
//             <button className="tf-file-clear" onClick={e => { e.stopPropagation(); handleFileChange(null) }}>×</button>
//           </div>
//         ) : (
//           <div className="tf-file-empty">
//             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8">
//               <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
//             </svg>
//             <span>{hint || 'Click to upload'}</span>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// const TenantFormPage = () => {

//   const [pgId, setPgId] = useState(null);

//   useEffect(() => {
//     // URL se encrypted parameter lo
//     const path = window.location.pathname;
//     const encryptedParam = path.split('/').pop(); // /tenant-form/encrypted-data-here
//     console.log("encryptedParam",encryptedParam);
    
//     // Decrypt karo
//     const decryptedPgId = decryptPgId(encryptedParam);
    
//     // ✅ Console mein dikhao
//     console.log('🔓 Decrypted PG ID:', decryptedPgId);
    
//     setPgId(decryptedPgId);
    
//     // Ab form submit karte time ye PG ID use karo
//   }, []);

//   // const decryptPgId = (encryptedData) => {
//   //   try {
//   //     // URL decode karo pehle
//   //     const decoded = decodeURIComponent(encryptedData);
      
//   //     // AES decrypt (same key as backend)
//   //     const bytes = CryptoJS.AES.decrypt(decoded, 'cipl');
//   //     const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      
//   //     return decrypted;
//   //   } catch (error) {
//   //     console.error('Decryption failed:', error);
//   //     return null;
//   //   }
//   // };

// //   const decryptPgId = (encryptedData) => {
// //   try {
// //     // URL decode karo
// //     const decoded = decodeURIComponent(encryptedData);
    
// //     // Backend se data format: "IV:EncryptedData"
// //     const [ivHex, encryptedHex] = decoded.split(':');
    
// //     // Convert IV from hex to WordArray
// //     const iv = CryptoJS.enc.Hex.parse(ivHex);
    
// //     // Convert encrypted data from hex to WordArray
// //     const encrypted = CryptoJS.enc.Hex.parse(encryptedHex);
    
// //     // Create cipher params
// //     const cipherParams = CryptoJS.lib.CipherParams.create({
// //       ciphertext: encrypted,
// //       iv: iv,
// //       mode: CryptoJS.mode.CBC,
// //       padding: CryptoJS.pad.Pkcs7
// //     });
    
// //     // Decrypt (same key as backend)
// //     const decrypted = CryptoJS.AES.decrypt(
// //       cipherParams,
// //       CryptoJS.enc.Utf8.parse('cipl'), // Key as WordArray
// //       {
// //         iv: iv,
// //         mode: CryptoJS.mode.CBC,
// //         padding: CryptoJS.pad.Pkcs7
// //       }
// //     );
    
// //     const result = decrypted.toString(CryptoJS.enc.Utf8);
// //     console.log('🔓 Decrypted PG ID:', result);
// //     return result;
// //   } catch (error) {
// //     console.error('Decryption failed:', error);
// //     return null;
// //   }
// // };

// // const decryptPgId = (encryptedData) => {
// //     try {
// //       // URL decode
// //       const decoded = decodeURIComponent(encryptedData);
      
// //       // Split IV and encrypted data
// //       const [ivHex, encryptedHex] = decoded.split(':');
      
// //       console.log("IV Hex:", ivHex);
// //       console.log("Encrypted Hex:", encryptedHex);
      
// //       // Convert hex to WordArray
// //       const iv = CryptoJS.enc.Hex.parse(ivHex);
// //       const encrypted = CryptoJS.enc.Hex.parse(encryptedHex);
      
// //       // Secret key (same as backend)
// //       const secretKey = 'cipl';
// //       const key = CryptoJS.SHA256(secretKey);
      
// //       // Decrypt
// //       const decrypted = CryptoJS.AES.decrypt(
// //         {
// //           ciphertext: encrypted
// //         },
// //         key,
// //         {
// //           iv: iv,
// //           mode: CryptoJS.mode.CBC,
// //           padding: CryptoJS.pad.Pkcs7
// //         }
// //       );
      
// //       const result = decrypted.toString(CryptoJS.enc.Utf8);
// //       return result;
// //     } catch (error) {
// //       console.error('Decryption failed:', error);
// //       return null;
// //     }
// //   };


// const decryptPgId = (encryptedData) => {
//   try {
//     // URL decode
//     const decoded = decodeURIComponent(encryptedData);
//     console.log("Decoded:", decoded);
    
//     // Split IV and encrypted data
//     const [ivHex, encryptedHex] = decoded.split(':');
    
//     console.log("IV Hex:", ivHex);
//     console.log("Encrypted Hex:", encryptedHex);
    
//     // Convert hex to WordArray
//     const iv = CryptoJS.enc.Hex.parse(ivHex);
//     const encrypted = CryptoJS.enc.Hex.parse(encryptedHex);
    
//     // Secret key - same as backend (sha256 of 'cipl')
//     const secretKey = 'cipl';
//     const key = CryptoJS.SHA256(secretKey);
    
//     // Create encrypted object
//     const encryptedObj = CryptoJS.lib.CipherParams.create({
//       ciphertext: encrypted,
//       iv: iv,
//       mode: CryptoJS.mode.CBC,
//       padding: CryptoJS.pad.Pkcs7,
//       key: key
//     });
    
//     // Decrypt
//     const decrypted = CryptoJS.AES.decrypt(encryptedObj, key, {
//       iv: iv,
//       mode: CryptoJS.mode.CBC,
//       padding: CryptoJS.pad.Pkcs7
//     });
    
//     const result = decrypted.toString(CryptoJS.enc.Utf8);
//     console.log('🔓 Decrypted:', result);
//     return result;
//   } catch (error) {
//     console.error('Decryption failed:', error);
//     return null;
//   }
// };


//   // const { pgId } = useParams();
//   const navigate = useNavigate();
//   const [pgDetails, setPgDetails] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const [apiError, setApiError] = useState(null);
//   const [success, setSuccess] = useState(false);

//   // Form state
//   const [form, setForm] = useState({
//     name: '', age: '', dob: '', gender: '', phone: '', email: '',
//     presentAddress: '', permanentAddress: '',
//     emergencyName: '', emergencyPhone: '', emergencyRelation: '',
//     profession: '', checkInDate: '',
//     aadhaarFile: null, photoFile: null, idFile: null,
//   });

//   const [errors, setErrors] = useState({});

//   // Fetch PG details on load
//   useEffect(() => {
//     const fetchPGDetails = async () => {
//       try {
//         setLoading(true);
//         const result = await getPGById(pgId);
//         if (result.PGID ) {
//           setPgDetails(result);
//         } else {
//           setApiError('PG not found. Please check the link.');
//         }
//       } catch (error) {
//         console.error('Error fetching PG details:', error);
//         setApiError('Failed to load PG details. Please try again.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (pgId) {
//       fetchPGDetails();
//     }
//   }, [pgId]);

//   const set = (k, v) => {
//     setForm(p => ({ ...p, [k]: v }));
//     setErrors(p => ({ ...p, [k]: undefined }));
//   };

//   const setFile = (file, fieldName) => {
//     setForm(p => ({ ...p, [fieldName]: file }));
//   };

//   const validate = () => {
//     const e = {};
//     if (!form.name.trim()) e.name = 'Full name is required';
//     if (!form.phone.trim()) e.phone = 'Mobile number is required';
//     if (!form.email.trim()) e.email = 'Email is required';
//     if (!form.checkInDate) e.checkInDate = 'Check-in date is required';
//     if (!form.gender) e.gender = 'Please select gender';
//     return e;
//   };

//   const handleSubmit = async () => {
//     const validationErrors = validate();
//     if (Object.keys(validationErrors).length) {
//       setErrors(validationErrors);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//       return;
//     }

//     if (!pgId) {
//       setApiError('Invalid PG. Please try again.');
//       return;
//     }

//     setSubmitting(true);
//     setApiError(null);

//     try {
//       const formData = new FormData();

//       const tenantData = {
//         PGID: parseInt(pgId),
//         TenantName: form.name,
//         Tenantage: form.age ? parseInt(form.age) : null,
//         TenantDOB: form.dob || null,
//         Gender: form.gender ? form.gender.charAt(0).toUpperCase() + form.gender.slice(1) : null,
//         PhoneNumber: form.phone,
//         Email1: form.email || null,
//         PresentAddress: form.presentAddress || null,
//         PermanentAddress: form.permanentAddress || null,
//         EmergencyContactRelation: form.emergencyRelation || null,
//         EmergencyContactPersonName: form.emergencyName || null,
//         EmergencyContactNumber: form.emergencyPhone || null,
//         TenantProfession: form.profession || null,
//         CheckInDate: form.checkInDate,
//         JoiningDate: form.checkInDate,
//         Status: 'NEW',
//       };

//       formData.append('insertDto', JSON.stringify(tenantData));

//       if (form.aadhaarFile) formData.append('aadhaar', form.aadhaarFile);
//       if (form.photoFile) formData.append('photo', form.photoFile);
//       if (form.idFile) formData.append('workingId', form.idFile);

//       const result = await insert_tenant_with_files(formData);

//       if (result && (result.Success === 1 || result.statusCode === 201)) {
//         setSuccess(true);
//         setTimeout(() => {
//           navigate('/');
//         }, 3000);
//       } else if (result && result.statusCode === 413) {
//         setApiError('File size exceeds 5MB limit. Please upload smaller files.');
//       }else {
//         // Show the error message from API
//         let errorMsg = result?.Message  || result?.message || 'Failed to add tenant. Please try again.'
        
//         // Make it user-friendly for duplicate phone
//         if (errorMsg && errorMsg.includes('Violation of UNIQUE KEY')) {
//           errorMsg = 'This phone number is already registered. Please use a different phone number.'
//         }
        
//         setApiError(errorMsg)
//       }
//     } catch (error) {
//       console.error('Error submitting tenant:', error)
      
//       let errorMsg = error.message || 'Failed to add tenant. Please try again.'
      
//       // Make it user-friendly for duplicate phone
//       if (errorMsg && errorMsg.includes('Violation of UNIQUE KEY')) {
//         errorMsg = 'This phone number is already registered. Please use a different phone number.'
//       }
      
//       setApiError(errorMsg)
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="tf-loader-container">
//         <div className="tf-loader"></div>
//         <p>Loading form...</p>
//       </div>
//     );
//   }

//   if (success) {
//     return (
//       <div className="tf-success-container">
//         <div className="tf-success-card">
//           <div className="tf-success-icon">✓</div>
//           <h2>Application Submitted Successfully!</h2>
//           <p>Thank you for completing your registration. The property owner will review your application and contact you shortly.</p>
//           <button className="tf-btn tf-btn-primary" onClick={() => navigate('/')}>
//             Go to Homepage
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="tf-container">
//       <div className="tf-wrapper">
//         {/* Header */}
//         <div className="tf-header">
//           <h1>Tenant Registration Form</h1>
//           {pgDetails && (
//             <div className="tf-pg-info">
//               <strong>{pgDetails.PGName}</strong>
//               <span>{pgDetails.PGCity}, {pgDetails.PGLocality}</span>
//             </div>
//           )}
//         </div>

//         {/* Error Display */}
//         {apiError && (
//           <div className="tf-error-box">
//             <strong>Error:</strong> {apiError}
//           </div>
//         )}

//         <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
//           {/* Personal Information */}
//           <div className="tf-section">
//             <h3 className="tf-section-title">Personal Information</h3>
//             <div className="tf-grid">
//               <div className={`tf-field ${errors.name ? 'tf-field-error' : ''}`}>
//                 <label className="tf-label">Full Name <span className="tf-req">*</span></label>
//                 <input 
//                   type="text" 
//                   className="tf-input" 
//                   placeholder="Enter your full name"
//                   value={form.name} 
//                   onChange={e => set('name', e.target.value)} 
//                 />
//                 {errors.name && <span className="tf-error">{errors.name}</span>}
//               </div>

//               <div className="tf-field">
//                 <label className="tf-label">Age</label>
//                 <input 
//                   type="number" 
//                   className="tf-input" 
//                   placeholder="Age"
//                   value={form.age} 
//                   onChange={e => set('age', e.target.value)} 
//                 />
//               </div>

//               <div className="tf-field">
//                 <label className="tf-label">Date of Birth</label>
//                 <input 
//                   type="date" 
//                   className="tf-input" 
//                   value={form.dob} 
//                   onChange={e => set('dob', e.target.value)} 
//                 />
//               </div>

//               <div className={`tf-field ${errors.gender ? 'tf-field-error' : ''}`}>
//                 <label className="tf-label">Gender <span className="tf-req">*</span></label>
//                 <select className="tf-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
//                   <option value="">Select gender</option>
//                   <option value="male">Male</option>
//                   <option value="female">Female</option>
//                   <option value="other">Other</option>
//                 </select>
//                 {errors.gender && <span className="tf-error">{errors.gender}</span>}
//               </div>

//               <div className={`tf-field ${errors.phone ? 'tf-field-error' : ''}`}>
//                 <label className="tf-label">Mobile Number <span className="tf-req">*</span></label>
//                 <input 
//                   type="tel" 
//                   className="tf-input" 
//                   placeholder="+91 98765 43210"
//                   value={form.phone} 
//                   onChange={e => set('phone', e.target.value)} 
//                 />
//                 {errors.phone && <span className="tf-error">{errors.phone}</span>}
//               </div>

//               <div className={`tf-field ${errors.email ? 'tf-field-error' : ''}`}>
//                 <label className="tf-label">Email Address <span className="tf-req">*</span></label>
//                 <input 
//                   type="email" 
//                   className="tf-input" 
//                   placeholder="your@email.com"
//                   value={form.email} 
//                   onChange={e => set('email', e.target.value)} 
//                 />
//                 {errors.email && <span className="tf-error">{errors.email}</span>}
//               </div>

//               <div className="tf-field tf-field-full">
//                 <label className="tf-label">Present Address</label>
//                 <textarea 
//                   className="tf-textarea" 
//                   rows="3" 
//                   placeholder="House No., Street, City, State, PIN Code"
//                   value={form.presentAddress} 
//                   onChange={e => set('presentAddress', e.target.value)} 
//                 />
//               </div>

//               <div className="tf-field tf-field-full">
//                 <label className="tf-label">Permanent Address</label>
//                 <textarea 
//                   className="tf-textarea" 
//                   rows="3" 
//                   placeholder="House No., Street, City, State, PIN Code"
//                   value={form.permanentAddress} 
//                   onChange={e => set('permanentAddress', e.target.value)} 
//                 />
//               </div>
//             </div>
//           </div>

//           {/* Emergency Contact */}
//           <div className="tf-section">
//             <h3 className="tf-section-title">Emergency Contact</h3>
//             <div className="tf-grid">
//               <div className="tf-field">
//                 <label className="tf-label">Contact Person Name</label>
//                 <input 
//                   type="text" 
//                   className="tf-input" 
//                   placeholder="Full name"
//                   value={form.emergencyName} 
//                   onChange={e => set('emergencyName', e.target.value)} 
//                 />
//               </div>

//               <div className="tf-field">
//                 <label className="tf-label">Contact Number</label>
//                 <input 
//                   type="tel" 
//                   className="tf-input" 
//                   placeholder="+91 98765 43210"
//                   value={form.emergencyPhone} 
//                   onChange={e => set('emergencyPhone', e.target.value)} 
//                 />
//               </div>

//               <div className="tf-field">
//                 <label className="tf-label">Relation</label>
//                 <select className="tf-select" value={form.emergencyRelation} onChange={e => set('emergencyRelation', e.target.value)}>
//                   <option value="">Select relation</option>
//                   <option value="parent">Parent</option>
//                   <option value="sibling">Sibling</option>
//                   <option value="spouse">Spouse</option>
//                   <option value="friend">Friend</option>
//                   <option value="other">Other</option>
//                 </select>
//               </div>
//             </div>
//           </div>

//           {/* Professional Details */}
//           <div className="tf-section">
//             <h3 className="tf-section-title">Professional Details</h3>
//             <div className="tf-grid">
//               <div className="tf-field">
//                 <label className="tf-label">Profession</label>
//                 <select className="tf-select" value={form.profession} onChange={e => set('profession', e.target.value)}>
//                   <option value="">Select profession</option>
//                   <option value="student">Student</option>
//                   <option value="working">Working Professional</option>
//                   <option value="business">Business / Self-employed</option>
//                   <option value="other">Other</option>
//                 </select>
//               </div>

//               <div className={`tf-field ${errors.checkInDate ? 'tf-field-error' : ''}`}>
//                 <label className="tf-label">Expected Check-in Date <span className="tf-req">*</span></label>
//                 <input 
//                   type="date" 
//                   className="tf-input" 
//                   value={form.checkInDate} 
//                   onChange={e => set('checkInDate', e.target.value)} 
//                 />
//                 {errors.checkInDate && <span className="tf-error">{errors.checkInDate}</span>}
//               </div>
//             </div>
//           </div>

//           {/* Documents */}
//           <div className="tf-section">
//             <h3 className="tf-section-title">Documents</h3>
//             <div className="tf-grid tf-grid-3">
//               <FileInput
//                 label="Aadhaar Card"
//                 accept=".pdf,.jpg,.jpeg,.png"
//                 value={form.aadhaarFile}
//                 onChange={setFile}
//                 fieldName="aadhaarFile"
//                 hint="PDF or Image (Max 5MB)"
//               />
//               <FileInput
//                 label="Passport Size Photo"
//                 accept=".jpg,.jpeg,.png"
//                 value={form.photoFile}
//                 onChange={setFile}
//                 fieldName="photoFile"
//                 hint="JPG/PNG (Max 5MB)"
//               />
//               <FileInput
//                 label="Work/Student ID Card"
//                 accept=".pdf,.jpg,.jpeg,.png"
//                 value={form.idFile}
//                 onChange={setFile}
//                 fieldName="idFile"
//                 hint="PDF or Image (Max 5MB)"
//               />
//             </div>
//           </div>

//           {/* Submit Button */}
//           <div className="tf-footer">
//             <button type="button" className="tf-btn tf-btn-outline" onClick={() => navigate('/')}>
//               Cancel
//             </button>
//             <button type="submit" className="tf-btn tf-btn-primary" disabled={submitting}>
//               {submitting ? (
//                 <>
//                   <span className="tf-spinner"></span>
//                   Submitting...
//                 </>
//               ) : (
//                 'Submit Application'
//               )}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default TenantFormPage;